import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  calcularTroco,
  FORMA_PAGAMENTO_ROTULO,
  FORMAS_PAGAMENTO,
  formatarDataHora,
  formatarDataHoraSegundos,
  formatarReais,
  type FormaPagamento,
  type VendaAjustarEntrada,
} from "@caixaflow/shared";
import { CHAVE_CAIXA_ATUAL } from "../api/caixa";
import { api, ErroApi } from "../api/cliente";
import { useSessao } from "../api/sessao";
import type { FotoVenda, Venda } from "../api/tipos";
import { CamposAutorizacao, useAutorizacaoAdmin } from "./AutorizacaoAdmin";
import { Campo, EntradaReais } from "./Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "./Estados";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

/** Depois de cancelar ou ajustar: caixa, cardápio (saldo), listas e relatórios ficam desatualizados. */
export function recarregarDepoisDaVenda(qc: QueryClient, venda: { id: string; caixaId: string; eventoId: string }) {
  qc.invalidateQueries({ queryKey: ["venda", venda.id] });
  qc.invalidateQueries({ queryKey: ["vendas", venda.caixaId] });
  qc.invalidateQueries({ queryKey: ["cardapio", venda.eventoId] });
  qc.invalidateQueries({ queryKey: ["caixa", venda.caixaId] });
  qc.invalidateQueries({ queryKey: CHAVE_CAIXA_ATUAL });
  qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("relatorio") });
}

type Modo = "detalhe" | "cancelar" | "ajustar";

/**
 * Detalhe de uma venda com as ações de cancelar e ajustar. O operador age nas próprias vendas
 * com o caixa aberto (com autorização de ADMIN); o ADMIN age em qualquer venda de evento não
 * encerrado, inclusive de caixa já fechado (o fechamento é recalculado).
 */
export function ModalVenda({ vendaId, inicial, onFechar }: { vendaId: string | null; inicial?: Venda; onFechar: () => void }) {
  const [modo, setModo] = useState<Modo>("detalhe");
  const fechar = () => {
    setModo("detalhe");
    onFechar();
  };
  const titulo = modo === "cancelar" ? "Cancelar venda" : modo === "ajustar" ? "Ajustar venda" : "Venda";
  return (
    <Modal aberto={vendaId !== null} titulo={titulo} onFechar={fechar}>
      {vendaId && <ConteudoVenda key={vendaId} vendaId={vendaId} inicial={inicial} modo={modo} setModo={setModo} onFechar={fechar} />}
    </Modal>
  );
}

function ConteudoVenda(props: { vendaId: string; inicial?: Venda; modo: Modo; setModo: (m: Modo) => void; onFechar: () => void }) {
  const { vendaId, inicial, modo, setModo, onFechar } = props;
  const { data: sessao } = useSessao();
  const venda = useQuery({
    queryKey: ["venda", vendaId],
    queryFn: () => api<Venda>(`/vendas/${vendaId}`),
    placeholderData: inicial,
  });

  if (venda.isPending) return <Carregando />;
  if (venda.error && !venda.data) return <FalhaAoCarregar erro={venda.error} tentarDeNovo={() => venda.refetch()} />;
  const v = venda.data!;
  const souAdmin = sessao?.usuario.perfil === "ADMIN";
  const caixaFechado = v.caixa.status !== "ABERTO";
  const podeAlterar =
    v.status === "VALIDA" &&
    v.evento.status !== "ENCERRADO" &&
    v.caixa.origem === "PDV" &&
    (souAdmin || (!caixaFechado && v.operadorId === sessao?.usuario.id));

  if (modo === "cancelar") return <FormCancelar venda={v} caixaFechado={caixaFechado} onVoltar={() => setModo("detalhe")} onPronto={onFechar} />;
  if (modo === "ajustar") return <FormAjustar venda={v} caixaFechado={caixaFechado} onVoltar={() => setModo("detalhe")} onPronto={onFechar} />;

  return (
    <div className="form">
      <ResumoVenda venda={v} />
      {v.status === "VALIDA" && !podeAlterar && (
        <p className="mudo pequeno" style={{ margin: 0 }}>
          {v.evento.status === "ENCERRADO"
            ? "Evento encerrado: a venda fica só para consulta."
            : v.caixa.origem === "IMPORTACAO"
              ? "Venda importada do protótipo: só para consulta."
              : "O caixa desta venda já foi fechado. Só a coordenação (ADMIN) pode cancelar ou ajustar."}
        </p>
      )}
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onFechar}>
          Fechar
        </button>
        {podeAlterar && (
          <>
            <button type="button" className="btn fantasma" onClick={() => setModo("ajustar")}>
              Ajustar
            </button>
            <button type="button" className="btn perigo" onClick={() => setModo("cancelar")}>
              Cancelar venda
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const descreverItens = (itens: { quantidade: number; nome?: string; nomeProduto?: string }[]) =>
  itens.map((i) => `${i.quantidade}× ${i.nome ?? i.nomeProduto}`).join(", ");

const descreverFoto = (f: FotoVenda) => `${FORMA_PAGAMENTO_ROTULO[f.formaPagamento]} · ${formatarReais(f.totalCentavos)} · ${descreverItens(f.itens)}`;

function ResumoVenda({ venda: v }: { venda: Venda }) {
  return (
    <>
      <div className="card pad" style={{ background: "var(--bg)" }}>
        <div className="linha">
          <b>
            Venda #{v.numero} · {formatarDataHoraSegundos(v.criadaEm)}
          </b>
          <span className="linha-esq">
            <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.formaPagamento]}</span>
            {v.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
            {v.ajustes.length > 0 && <span className="chip alerta">ajustada</span>}
            {v.viaFilaOffline && <span className="chip neutro">enviada depois (sem internet)</span>}
          </span>
        </div>
        <div className="mudo pequeno">
          {v.operador.nome} ·{" "}
          <Link to={`/caixas/${v.caixaId}`}>Caixa #{v.caixa.numero}</Link>
          {v.caixa.status !== "ABERTO" && " (fechado)"} · {v.evento.nome}
        </div>
      </div>
      <div className="card tbl-wrap">
        <table>
          <tbody>
            {v.itens.map((i) => (
              <tr key={i.produtoId}>
                <td className="quebra">
                  {i.quantidade}× {i.nomeProduto}
                  <div className="mudo pequeno">{formatarReais(i.precoUnitCentavos)} cada</div>
                </td>
                <td className="r">{formatarReais(i.subtotalCentavos)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="r">{formatarReais(v.totalCentavos)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {v.formaPagamento === "DINHEIRO" && v.recebidoCentavos !== null && (
        <span className="mudo pequeno">
          Recebido {formatarReais(v.recebidoCentavos)} · troco {formatarReais(v.trocoCentavos ?? 0)}
        </span>
      )}
      {v.aceiteAutorizadoPor && (
        <span className="mudo pequeno">Aceita com divergência ({v.divergencias.join(", ")}), autorizada por {v.aceiteAutorizadoPor.nome}.</span>
      )}
      {v.cancelamento && (
        <div className="aviso erro">
          Cancelada em {formatarDataHora(v.cancelamento.canceladaEm)}: {v.cancelamento.motivo}. Autorizado por{" "}
          {v.cancelamento.autorizadoPor.nome}
          {v.cancelamento.solicitadoPor.id !== v.cancelamento.autorizadoPor.id && `, a pedido de ${v.cancelamento.solicitadoPor.nome}`}.
        </div>
      )}
      {v.ajustes.length > 0 && (
        <div>
          <h4 style={{ margin: "4px 0 6px" }}>Ajustes</h4>
          <div className="card">
            {v.ajustes.map((a) => (
              <div key={a.id} className="venda-linha" style={{ gridTemplateColumns: "1fr" }}>
                <span className="pequeno">
                  <b>{formatarDataHora(a.em)}</b> · {a.motivo} · autorizado por {a.autorizadoPor.nome}
                  {a.caixaFechado && " · depois do fechamento"}
                  <div className="mudo">Antes: {descreverFoto(a.antes)}</div>
                  <div className="mudo">Depois: {descreverFoto(a.depois)}</div>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function AvisoCaixaFechado({ caixaFechado }: { caixaFechado: boolean }) {
  if (!caixaFechado) return null;
  return (
    <div className="aviso alerta">
      O caixa desta venda já foi fechado. O esperado do fechamento será recalculado; o contado continua o mesmo, e a diferença
      passa a mostrar o efeito.
    </div>
  );
}

function FormCancelar(props: { venda: Venda; caixaFechado: boolean; onVoltar: () => void; onPronto: () => void }) {
  const { venda, caixaFechado, onVoltar, onPronto } = props;
  const [motivo, setMotivo] = useState("");
  const autorizacao = useAutorizacaoAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const cancelar = useMutation({
    mutationFn: () => api<Venda>(`/vendas/${venda.id}/cancelar`, "POST", { motivo, autorizacao: autorizacao.valor }),
    onSuccess: () => {
      toast(`Venda #${venda.numero} cancelada`, "info");
      recarregarDepoisDaVenda(qc, venda);
      onPronto();
    },
    onError: () => autorizacao.limparSegredo(),
  });
  const erro = cancelar.error instanceof ErroApi ? cancelar.error : null;
  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        cancelar.mutate();
      }}
    >
      <div className="card pad" style={{ background: "var(--bg)" }}>
        <b>
          Venda #{venda.numero} · {formatarReais(venda.totalCentavos)}
        </b>
        <div className="mudo pequeno">
          {descreverItens(venda.itens)} · {FORMA_PAGAMENTO_ROTULO[venda.formaPagamento]}
        </div>
      </div>
      <p className="mudo pequeno" style={{ margin: 0 }}>
        A venda não é apagada: fica como cancelada, com o motivo e quem autorizou, e o estoque volta.
      </p>
      <AvisoCaixaFechado caixaFechado={caixaFechado} />
      {cancelar.error && !erro?.campos.length && (
        <div className="aviso erro" role="alert">
          {mensagemDeErro(cancelar.error)}
        </div>
      )}
      <Campo rotulo="Motivo" erro={erro?.doCampo("motivo")}>
        <input required minLength={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: cliente desistiu" />
      </Campo>
      <CamposAutorizacao autorizacao={autorizacao} erro={(c) => erro?.doCampo(c)} />
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onVoltar}>
          Voltar
        </button>
        <button type="submit" className="btn perigo" disabled={cancelar.isPending}>
          {cancelar.isPending ? "Conferindo…" : "Cancelar venda"}
        </button>
      </div>
    </form>
  );
}

function FormAjustar(props: { venda: Venda; caixaFechado: boolean; onVoltar: () => void; onPronto: () => void }) {
  const { venda, caixaFechado, onVoltar, onPronto } = props;
  const [forma, setForma] = useState<FormaPagamento>(venda.formaPagamento);
  const [quantidades, setQuantidades] = useState<Record<string, number>>(() =>
    Object.fromEntries(venda.itens.map((i) => [i.produtoId, i.quantidade])),
  );
  const [recebido, setRecebido] = useState<number | null>(venda.formaPagamento === "DINHEIRO" ? venda.recebidoCentavos : null);
  const [motivo, setMotivo] = useState("");
  const autorizacao = useAutorizacaoAdmin();
  const qc = useQueryClient();
  const toast = useToast();

  const total = venda.itens.reduce((s, i) => s + (quantidades[i.produtoId] ?? 0) * i.precoUnitCentavos, 0);
  const sobraAlgum = venda.itens.some((i) => (quantidades[i.produtoId] ?? 0) > 0);
  const mudouItens = venda.itens.some((i) => quantidades[i.produtoId] !== i.quantidade);
  const recebidoFinal = forma === "DINHEIRO" ? recebido : null;
  const mudou = mudouItens || forma !== venda.formaPagamento || recebidoFinal !== venda.recebidoCentavos;
  const troco = recebidoFinal !== null ? calcularTroco(total, recebidoFinal) : null;
  const faltaDinheiro = recebidoFinal !== null && troco === null;

  const ajustar = useMutation({
    mutationFn: () => {
      const corpo: VendaAjustarEntrada = { motivo, autorizacao: autorizacao.valor };
      if (forma !== venda.formaPagamento) corpo.formaPagamento = forma;
      if (forma === "DINHEIRO") corpo.recebidoCentavos = recebido;
      const itens = venda.itens.filter((i) => quantidades[i.produtoId] !== i.quantidade);
      if (itens.length) corpo.itens = itens.map((i) => ({ produtoId: i.produtoId, quantidade: quantidades[i.produtoId] ?? 0 }));
      return api<Venda>(`/vendas/${venda.id}/ajustar`, "POST", corpo);
    },
    onSuccess: (v) => {
      toast(`Venda #${v.numero} ajustada`);
      recarregarDepoisDaVenda(qc, venda);
      onPronto();
    },
    onError: () => autorizacao.limparSegredo(),
  });
  const erro = ajustar.error instanceof ErroApi ? ajustar.error : null;
  const mudar = (produtoId: string, delta: number, maximo: number) =>
    setQuantidades((q) => ({ ...q, [produtoId]: Math.min(maximo, Math.max(0, (q[produtoId] ?? 0) + delta)) }));

  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (mudou && sobraAlgum && !faltaDinheiro) ajustar.mutate();
      }}
    >
      <p className="mudo pequeno" style={{ margin: 0 }}>
        Troque a forma de pagamento ou tire itens (o que sair volta ao estoque). Para acrescentar itens, registre uma venda nova.
      </p>
      <AvisoCaixaFechado caixaFechado={caixaFechado} />
      <div>
        <span className="rotulo-campo">Forma de pagamento</span>
        <div className="pays" role="group" aria-label="Forma de pagamento">
          {FORMAS_PAGAMENTO.map((f) => (
            <button key={f} type="button" aria-pressed={forma === f} onClick={() => setForma(f)}>
              {FORMA_PAGAMENTO_ROTULO[f]}
            </button>
          ))}
        </div>
      </div>
      <div className="card">
        {venda.itens.map((i) => {
          const q = quantidades[i.produtoId] ?? 0;
          return (
            <div key={i.produtoId} className="linha item-ajuste">
              <span className={q === 0 ? "riscado" : ""}>
                {i.nomeProduto}
                <span className="mudo pequeno"> · {formatarReais(i.precoUnitCentavos)}</span>
              </span>
              <span className="linha-esq" style={{ flexWrap: "nowrap" }}>
                <button type="button" className="btn pequeno fantasma" aria-label={`Tirar um ${i.nomeProduto}`} disabled={q === 0} onClick={() => mudar(i.produtoId, -1, i.quantidade)}>
                  −
                </button>
                <b style={{ minWidth: 22, textAlign: "center" }}>{q}</b>
                <button
                  type="button"
                  className="btn pequeno fantasma"
                  aria-label={`Devolver um ${i.nomeProduto}`}
                  disabled={q >= i.quantidade}
                  onClick={() => mudar(i.produtoId, 1, i.quantidade)}
                >
                  +
                </button>
              </span>
            </div>
          );
        })}
        <div className="linha item-ajuste">
          <b>Novo total</b>
          <b>
            {formatarReais(total)}
            {total !== venda.totalCentavos && <span className="mudo pequeno"> (era {formatarReais(venda.totalCentavos)})</span>}
          </b>
        </div>
      </div>
      {!sobraAlgum && <div className="aviso erro">Para tirar todos os itens, cancele a venda.</div>}
      {forma === "DINHEIRO" && (
        <div className="duas-colunas">
          <Campo rotulo="Recebido (R$)" dica="opcional" erro={erro?.doCampo("recebidoCentavos")}>
            <EntradaReais centavos={recebido} onChange={setRecebido} />
          </Campo>
          <div className="campo">
            <span>Troco</span>
            <b className={faltaDinheiro ? "falta" : ""}>
              {recebido === null ? "—" : faltaDinheiro ? `faltam ${formatarReais(total - recebido)}` : formatarReais(troco ?? 0)}
            </b>
          </div>
        </div>
      )}
      {ajustar.error && !erro?.campos.length && (
        <div className="aviso erro" role="alert">
          {mensagemDeErro(ajustar.error)}
        </div>
      )}
      <Campo rotulo="Motivo" erro={erro?.doCampo("motivo")}>
        <input required minLength={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: pagou em dinheiro, não Pix" />
      </Campo>
      <CamposAutorizacao autorizacao={autorizacao} erro={(c) => erro?.doCampo(c)} />
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onVoltar}>
          Voltar
        </button>
        <button type="submit" className="btn primario" disabled={ajustar.isPending || !mudou || !sobraAlgum || faltaDinheiro}>
          {ajustar.isPending ? "Conferindo…" : mudou ? "Salvar ajuste" : "Nada mudou"}
        </button>
      </div>
    </form>
  );
}
